import React from "react";

import { FormProvider, useForm } from "react-hook-form";

import { AdvisorInfoSchema } from "@fartherfinance/frontend/api/Entity/Types";
import { useGetTransitionDetailsDto } from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useGetTransitionDetailsDto";
import { UpdateTransitionRequest } from "@fartherfinance/frontend/api/TransitionWorkflows/requests/patchTransition";
import {
  FamOnboardingStatus,
  OnboardingStatus,
  RegistrationStatus,
  Sentiment,
  TransitionDetailsDTO,
  TransitionPhase,
  TransitionStatus,
  TransitionType,
} from "@fartherfinance/frontend/api/TransitionWorkflows/Types";
import { Custodian } from "@fartherfinance/frontend/api/Types";

import { custodianOptions } from "../../Tasks/common";
import { useAdvisorsOptions } from "../../Tasks/hooks";
import {
  famOnboardingStatusMap,
  onboardingStatusMap,
  registrationStatusMap,
  selectFirstTransitionAdvisor,
  sentimentMap,
  transitionPhaseMap,
  transitionStatusMap,
} from "../utils";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { EnhancedAutocompleteOption } from "@src/multiCustodian/pages/Advisor/common";
import { transitionTypeOptions } from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/common";
import { ComboboxOption } from "@src/sharedComponents/Combobox/Combobox";
import FormBody from "@src/sharedComponents/Forms/FormBody";
import FormBodySpanAllColumns from "@src/sharedComponents/Forms/FormBodySpanAllColumns";
import FormComboboxField from "@src/sharedComponents/Forms/FormComboboxField";
import FormDateField from "@src/sharedComponents/Forms/FormDateField";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormSection from "@src/sharedComponents/Forms/FormSection";
import FormTextareaField from "@src/sharedComponents/Forms/FormTextareaField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "@src/multiCustodian/pages/Advisor/Tasks/CreateTask/CreateTask.module.css";

// Interface describing shape of the edit transition form data
interface TransitionFormData {
  // Transition Overview
  launchDate: Date | null;
  team: string;
  advisors: ComboboxOption<string>[];
  email: string;
  transitionStatus: EnhancedAutocompleteOption<TransitionStatus> | null;
  onboardingStatus: EnhancedAutocompleteOption<OnboardingStatus> | null;
  sentiment: EnhancedAutocompleteOption<Sentiment> | null;
  clientTrackerUrl: string;

  // Text areas
  highlightsAndLowlights: string;
  recentUpdates: string;
  operationalNotes: string;

  // Points of Contact
  cxTeamLead: string;
  onboardingManager: string;
  marketingOwner: string;
  executiveSponsor: string;

  // Transition Details
  registrationStatus: EnhancedAutocompleteOption<RegistrationStatus> | null;
  advWDeadline: Date | null;
  transitionType: EnhancedAutocompleteOption<TransitionType> | null;
  custodian: EnhancedAutocompleteOption<Custodian> | null;
  custodianRepCode: string;
  priorFirm: string;
  additionalEmployees: string;
  transitionPhase: EnhancedAutocompleteOption<TransitionPhase> | null;
  households: string;
  docsSentPercent: string;
  docsSignedPercent: string;
  portalsCreatedPercent: string;
  billing: string;
  bdBulkRequest: Date | null;
  famOnboardingStatus: EnhancedAutocompleteOption<FamOnboardingStatus> | null;
  expectedAum: string;
  transitionedAum: string;
}

const transitionStatusOptions: { label: string; value: TransitionStatus }[] =
  Object.entries(transitionStatusMap).map(([value, label]) => ({
    label,
    value: value as TransitionStatus,
  }));

const onboardingStatusOptions: { label: string; value: OnboardingStatus }[] =
  Object.entries(onboardingStatusMap).map(([value, label]) => ({
    label,
    value: value as OnboardingStatus,
  }));

const sentimentOptions: { label: string; value: Sentiment }[] = Object.entries(
  sentimentMap
).map(([value, label]) => ({
  label,
  value: value as Sentiment,
}));

const registrationStatusOptions: {
  label: string;
  value: RegistrationStatus;
}[] = Object.entries(registrationStatusMap).map(([value, label]) => ({
  label,
  value: value as RegistrationStatus,
}));

const transitionPhaseOptions: { label: string; value: TransitionPhase }[] =
  Object.entries(transitionPhaseMap).map(([value, label]) => ({
    label,
    value: value as TransitionPhase,
  }));

const famOnboardingStatusOptions: {
  label: string;
  value: FamOnboardingStatus;
}[] = Object.entries(famOnboardingStatusMap).map(([value, label]) => ({
  label,
  value: value as FamOnboardingStatus,
}));

// Export form content component without drawer for use in other components
export const EditTransitionFormContent: React.FC<{
  transitionId: string;
  onFormReady?: (form: any) => void;
}> = ({ transitionId, onFormReady }) => {
  // Get advisors options
  const { advisorsOptions } = useAdvisorsOptions();

  const auth = useAdvisorRequestAuth();

  const {
    data: detailsData,
    isLoading: detailsLoading,
    hasError: detailsError,
  } = useGetTransitionDetailsDto(transitionId, auth);

  const mapDetailsToFormData = (
    details: TransitionDetailsDTO | undefined
  ): TransitionFormData => {
    if (!details) {
      return {
        launchDate: null,
        team: "",
        advisors: [],
        email: "",
        transitionStatus: null,
        onboardingStatus: null,
        sentiment: null,
        clientTrackerUrl: "",
        highlightsAndLowlights: "",
        recentUpdates: "",
        operationalNotes: "",
        cxTeamLead: "",
        onboardingManager: "",
        marketingOwner: "",
        executiveSponsor: "",
        registrationStatus: null,
        advWDeadline: null,
        transitionType: null,
        custodian: null,
        custodianRepCode: "",
        priorFirm: "",
        additionalEmployees: "",
        transitionPhase: null,
        households: "",
        docsSentPercent: "",
        docsSignedPercent: "",
        portalsCreatedPercent: "",
        billing: "",
        bdBulkRequest: null,
        famOnboardingStatus: null,
        expectedAum: "",
        transitionedAum: "",
      };
    }

    const formatAdvisors = (
      advisorData: AdvisorInfoSchema[]
    ): ComboboxOption<string>[] =>
      advisorData.map((advisor) => ({
        label: advisor.fullName,
        value: advisor.advisorId,
      }));

    // Helper function to find option by value
    const findOptionByValue = <T extends string>(
      options: EnhancedAutocompleteOption<T>[],
      value: T
    ): EnhancedAutocompleteOption<T> | null => {
      return options.find((option) => option.value === value) || null;
    };

    return {
      launchDate: details.task.dueDate ? details.task.dueDate : null,
      team: details.team ?? "",
      advisors: formatAdvisors(details.advisors),
      email: selectFirstTransitionAdvisor(details.advisors)?.emailAddress ?? "",
      transitionStatus: details.transitionStatus
        ? findOptionByValue(transitionStatusOptions, details.transitionStatus)
        : null,
      onboardingStatus: details.onboardingStatus
        ? findOptionByValue(onboardingStatusOptions, details.onboardingStatus)
        : null,
      sentiment: details.sentiment
        ? findOptionByValue(sentimentOptions, details.sentiment)
        : null,
      clientTrackerUrl: details.clientTrackerUrl ?? "",

      highlightsAndLowlights: details.highlightsAndLowlights ?? "",
      recentUpdates: details.recentUpdates ?? "",
      operationalNotes: details.operationalNotes ?? "",

      cxTeamLead: details.cxTeamLead ?? "",
      onboardingManager: details.onboardingManager ?? "",
      marketingOwner: details.marketingOwner ?? "",
      executiveSponsor: details.executiveSponsor ?? "",

      registrationStatus: details.registrationStatus
        ? findOptionByValue(
            registrationStatusOptions,
            details.registrationStatus
          )
        : findOptionByValue(
            registrationStatusOptions,
            RegistrationStatus.enum.NotRegistered
          ),
      advWDeadline: details.advWDeadline
        ? new Date(details.advWDeadline)
        : null,
      transitionType: details.transitionType
        ? findOptionByValue(transitionTypeOptions, details.transitionType)
        : null,
      custodian: details.custodian
        ? findOptionByValue(custodianOptions, details.custodian)
        : null,
      custodianRepCode: details.custodianRepCode ?? "",
      priorFirm: details.priorFirm ?? "",
      additionalEmployees: details.additionalEmployees?.toString() ?? "",
      transitionPhase: details.transitionPhase
        ? findOptionByValue(transitionPhaseOptions, details.transitionPhase)
        : findOptionByValue(
            transitionPhaseOptions,
            TransitionPhase.enum.NotStarted
          ),
      households: details.households?.toString() ?? "",
      docsSentPercent: details.docsSentPercent?.toString() ?? "",
      docsSignedPercent: details.docsSignedPercent?.toString() ?? "",
      portalsCreatedPercent: details.portalsCreatedPercent?.toString() ?? "",
      billing: details.billing ?? "",
      bdBulkRequest: details.bdBulkRequest
        ? new Date(details.bdBulkRequest)
        : null,
      famOnboardingStatus: details.famOnboardingStatus
        ? findOptionByValue(
            famOnboardingStatusOptions,
            details.famOnboardingStatus
          )
        : null,
      expectedAum: details.expectedAum?.toString() ?? "",
      transitionedAum: details.transitionedAum?.toString() ?? "",
    };
  };

  // Initialize form with data
  const form = useForm<TransitionFormData>({
    mode: "onChange",
    defaultValues: mapDetailsToFormData(detailsData),
  });

  // Pass form instance to parent component
  React.useEffect(() => {
    if (onFormReady) {
      onFormReady(form);
    }
  }, [form, onFormReady]);

  return (
    <FormProvider {...form}>
      <div className={styles.header}>
        <Text variant="title2">
          {detailsLoading
            ? "Loading…"
            : detailsError
            ? "Error loading details"
            : "Edit Transition Details"}
        </Text>
      </div>

      <Spacer verticalSpacing={"30px"} />

      <FormBody>
        {/* Transition Overview Section */}
        <FormSection
          title={
            <Text variant="title3" color="text">
              Transition Overview
            </Text>
          }
        >
          <FormDateField
            name="launchDate"
            label="Launch Date"
            placeholder="MM/DD/YYYY"
          />
          <FormTextField name="team" label="Advisor Team" />
          <FormComboboxField
            name="advisors"
            label="Advisor(s)"
            values={advisorsOptions}
          />
          <FormTextField name="email" label="Email" />
          <FormDropdownField
            name="transitionStatus"
            label="Transition Status"
            values={transitionStatusOptions}
          />
          <FormDropdownField
            name="onboardingStatus"
            label="Onboarding Status"
            values={onboardingStatusOptions}
          />
          <FormDropdownField
            name="sentiment"
            label="Sentiment"
            values={sentimentOptions}
          />
          <FormTextField name="clientTrackerUrl" label="Client Tracker URL" />

          <FormBodySpanAllColumns>
            <FormTextareaField
              name="highlightsAndLowlights"
              label="Highlights and Lowlights"
              placeholder="Enter highlights and lowlights..."
              minRows={1}
            />
          </FormBodySpanAllColumns>
          <FormBodySpanAllColumns>
            <FormTextareaField
              name="recentUpdates"
              label="Recent Updates"
              placeholder="Enter recent updates..."
              minRows={1}
            />
          </FormBodySpanAllColumns>
          <FormBodySpanAllColumns>
            <FormTextareaField
              name="operationalNotes"
              label="Operational Notes"
              placeholder="Enter operational notes..."
              minRows={1}
            />
          </FormBodySpanAllColumns>
        </FormSection>

        {/* Points of Contact Section */}
        <FormSection
          title={
            <Text variant="title3" color="text">
              Points of Contact
            </Text>
          }
        >
          <FormTextField name="cxTeamLead" label="CX Team Lead" />
          <FormTextField name="onboardingManager" label="Onboarding Manager" />
          <FormTextField name="marketingOwner" label="Marketing Owner" />
          <FormTextField name="executiveSponsor" label="Executive Sponsor" />
        </FormSection>

        {/* Transition Details Section */}
        <FormSection
          title={
            <Text variant="title3" color="text">
              Transition Details
            </Text>
          }
        >
          <FormDropdownField
            name="registrationStatus"
            label="Registration Status"
            values={registrationStatusOptions}
          />
          <FormDateField
            name="advWDeadline"
            label="ADV-W Deadline"
            placeholder="MM/DD/YYYY"
          />
          <FormDropdownField
            name="transitionType"
            label="Transition Type"
            values={transitionTypeOptions}
          />
          <FormDropdownField
            name="custodian"
            label="Custodian"
            values={custodianOptions}
            disabled={true}
          />
          <FormTextField name="custodianRepCode" label="Custodian Rep Code" />
          <FormTextField name="priorFirm" label="Prior Firm" />
          <FormTextField
            name="additionalEmployees"
            label="Additional Employees"
          />
          <FormDropdownField
            name="transitionPhase"
            label="Transition Phase"
            values={transitionPhaseOptions}
          />
          <FormTextField name="households" label="# Households" />
          <FormNumberField
            name="docsSentPercent"
            label="Docs Sent"
            endAdornment="%"
          />
          <FormNumberField
            name="docsSignedPercent"
            label="Docs Signed"
            endAdornment="%"
          />
          <FormNumberField
            name="portalsCreatedPercent"
            label="Portals Created"
            endAdornment="%"
          />
          <FormTextField name="billing" label="Billing" />
          <FormDateField
            name="bdBulkRequest"
            label="BD Bulk Request"
            placeholder="MM/DD/YYYY"
          />
          <FormDropdownField
            name="famOnboardingStatus"
            label="FAM Onboarding Status"
            values={famOnboardingStatusOptions}
          />
          <FormTextField name="expectedAum" label="Expected AUM" />
          <FormTextField name="transitionedAum" label="Transitioned AUM" />
        </FormSection>
      </FormBody>
    </FormProvider>
  );
};

export const transformFormDataForSave = (
  formData: TransitionFormData
): UpdateTransitionRequest => {
  return {
    executiveSponsor: formData.executiveSponsor || "",
    marketingOwner: formData.marketingOwner || "",
    onboardingManager: formData.onboardingManager || "",
    cxTeamLead: formData.cxTeamLead || "",
    bdBulkRequestSubmissionDate: formData.bdBulkRequest?.toDateString() || "",
    billing: formData.billing || "",
    priorFirm: formData.priorFirm || "",
    custodianRepCode: formData.custodianRepCode || "",
    operationalNotes: formData.operationalNotes || "",
    recentUpdates: formData.recentUpdates || "",
    highlightsAndLowlights: formData.highlightsAndLowlights || "",
    team: formData.team || "",
    portalsCreatedPercent: Number(formData.portalsCreatedPercent) || 0,
    docsSignedPercent: Number(formData.docsSignedPercent) || 0,
    docsSentPercent: Number(formData.docsSentPercent) || 0,
    transitionedAum: Number(formData.transitionedAum) || 0,
    expectedAum: Number(formData.expectedAum) || 0,
    bdBulkRequest: formData.bdBulkRequest?.toDateString() || "",
    households: Number(formData.households) || 0,
    additionalEmployees: Number(formData.additionalEmployees) || 0,
    advWDeadline: formData.advWDeadline?.toDateString() || "",
    transitionStatus: formData.transitionStatus?.value || undefined,
    onboardingStatus: formData.onboardingStatus?.value || undefined,
    sentiment: formData.sentiment?.value || undefined,
    registrationStatus: formData.registrationStatus?.value || undefined,
    transitionType: formData.transitionType?.value || undefined,
    transitionPhase: formData.transitionPhase?.value || undefined,
    famOnboardingStatus: formData.famOnboardingStatus?.value || undefined,
    // Task details
    advisors: formData.advisors
      .filter((advisor) => advisor.value)
      .map((advisor) => advisor.value!),
    launchDate: formData.launchDate?.toDateString() || undefined,
    custodian: formData.custodian?.value || undefined,
    clientTrackerUrl: formData.clientTrackerUrl || "",
  };
};
